import React, { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import LocationPercentage from './LocationPercentage';
import LocationInput from './LocationInput';
import LocationSearchResults from './LocationSearchResults';
import {
  DisabledSuggestions,
  getAllDisabledSuggestions,
  getDisabledSuggestions,
  LocationSuggestion,
} from './utils';
import { RequestState, requestInit } from 'shared/auth/hooks/useRequestState';

export type AddressValue = {
  address?: string;
  percent?: number; // old values
  percentage?: number;
  longitude?: number;
  latitude?: number;
  type: keyof LocationSuggestion;
  place_id?: string;
};

interface LocationsProps {
  addresses: AddressValue[];
  onChange: (addresses: AddressValue[]) => unknown;
}

const StyledDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  .columns {
    display: flex;
    .full-width {
      width: 100%;
    }
    .left-row {
      margin-right: 20px;
    }
    .right-row {
      margin-left: 20px;
    }
    @media only screen and (max-width: 920px) {
      flex-direction: column-reverse;
      .left-row {
        margin-right: 0px;
      }
      .right-row {
        margin-left: 0px;
        margin-bottom: 24px;
      }
    }
  }
`;

const Locations: React.FC<LocationsProps> = ({ addresses, onChange }) => {
  const [suggestions, setSuggestions] = useState<
    RequestState<LocationSuggestion>
  >(requestInit());
  const [selectedAddresses, setSelectedAddresses] = useState<AddressValue[]>(
    addresses,
  );

  const [disabledSuggestions, setDisabledSuggestions] = useState<
    DisabledSuggestions
  >({});

  const [sessionToken, setSessionToken] = useState<
    google.maps.places.AutocompleteSessionToken | null
  >(null);

  useEffect(() => {
    try {
      setSessionToken(new google.maps.places.AutocompleteSessionToken());
    } catch {
      // Google Maps not available
    }
  }, []);

  const getLatLngFromPlaceId = (
    placeId: string,
    token: google.maps.places.AutocompleteSessionToken | null,
    callback: (lat: number, lng: number) => void,
  ) => {
    const placesService = new google.maps.places.PlacesService(
      document.createElement('div'),
    );
    placesService.getDetails(
      { placeId, fields: ['geometry.location'], sessionToken: token ?? undefined },
      (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          callback(lat, lng);
        } else {
          console.error('Failed to get lat/lng', status);
        }
      },
    );
  };

  useEffect(() => {
    onChange(selectedAddresses);
  }, [selectedAddresses, onChange]);

  const handleAdd = useCallback(
    (address: AddressValue) => {
      if (address?.place_id) {
        const token = sessionToken;
        setSessionToken(new google.maps.places.AutocompleteSessionToken());
        getLatLngFromPlaceId(address.place_id, token, (lat, lng) => {
          setSelectedAddresses(s => [
            ...s,
            { ...address, latitude: lat, longitude: lng },
          ]);
          setDisabledSuggestions(s =>
            getDisabledSuggestions({ ...s }, address),
          );
        });
      } else {
        setSelectedAddresses(s => [...s, address]);
        setDisabledSuggestions(s => getDisabledSuggestions({ ...s }, address));
      }
    },
    [sessionToken],
  );
  const handleRemove = useCallback(
    (index: number) => {
      if (index === -1) return;
      setDisabledSuggestions(
        getAllDisabledSuggestions(selectedAddresses, index),
      );
      setSelectedAddresses(s => {
        const newState = [...s];
        newState.splice(index, 1);
        return newState;
      });
    },
    [selectedAddresses, setSelectedAddresses],
  );
  const handlePercentage = useCallback(
    (percentage: number | null, index: number) => {
      setSelectedAddresses(s => {
        const newState = [...s];
        if (!newState[index]) return s;
        newState[index].percentage = percentage || undefined;
        return newState;
      });
    },
    [setSelectedAddresses],
  );

  return (
    <StyledDiv>
      <LocationInput handleAdd={handleAdd} handleSuggestions={setSuggestions} sessionToken={sessionToken} />
      <div className="columns">
        <div className="full-width left-row">
          <LocationSearchResults
            selectedAddresses={selectedAddresses}
            suggestions={suggestions}
            handleAdd={handleAdd}
            handleRemove={handleRemove}
          />
        </div>
        <div className="full-width right-row">
          <LocationPercentage
            disabledSuggestions={disabledSuggestions}
            selectedAddresses={selectedAddresses}
            handlePercentage={handlePercentage}
            handleRemove={handleRemove}
          />
        </div>
      </div>
    </StyledDiv>
  );
};

export default Locations;
